import { apiClient, ApiResponse } from './api'

export interface Scan {
  _id: string
  name: string
  type: 'MANUAL' | 'AUTOMATED'
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'CANCELLED'
  testingType: 'BLACK_BOX' | 'GRAY_BOX'
  description: string
  projectId: string
  tenantId: string
  assetId: string
  createdAt: string
  createdBy: string | null
  updatedAt: string
  updatedBy: string | null
  deletedAt: string | null
  deletedBy: string | null
  __v: number
  asset?: {
    _id: string
    assetType: string
    name: string
  }
}

export interface CreateScanRequest {
  name: string
  type: 'MANUAL' | 'AUTOMATED'
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'CANCELLED'
  testingType: 'BLACK_BOX' | 'GRAY_BOX'
  description: string
  projectId: string
  tenantId: string
  assetId: string
}

export interface UpdateScanRequest {
  name?: string
  type?: 'MANUAL' | 'AUTOMATED'
  status?: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'CANCELLED'
  testingType?: 'BLACK_BOX' | 'GRAY_BOX'
  description?: string
}

export interface ApplicationProfileAttribute {
  key: string
  value: string
  _id?: string
}

export interface CreateApplicationProfileRequest {
  scanId: string
  tenantId: string
  projectId: string
  assetId: string
  applicationProfileAttributes: ApplicationProfileAttribute[]
}

export interface UpdateApplicationProfileRequest {
  applicationProfileAttributes: ApplicationProfileAttribute[]
}

export interface ApplicationProfileResponse {
  applicationProfileAttributes: ApplicationProfileAttribute[]
}

export interface ApplicationProfile {
  _id: string
  scanId: string
  tenantId: string
  projectId: string
  assetId: string
  applicationProfileAttributes: ApplicationProfileAttribute[]
  createdAt: string
  updatedAt: string
}

export interface GetScansParams {
  page?: number
  limit?: number
  search?: string
  assetId?: string
}

export interface ScansPaginatedResponse {
  success: boolean
  message: string
  data: {
    data: Scan[]
    total: number
    page: number
    limit: number
  }
}

class ScansApiService {
  private baseEndpoint = '/projects-service/api/v1/scans'

  async getScans(params?: GetScansParams): Promise<ApiResponse<Scan[]>> {
    try {
      const queryParams = new URLSearchParams()

      if (params?.page) queryParams.append('page', params.page.toString())
      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.search) queryParams.append('search', params.search)

      const response = await apiClient.get<ScansPaginatedResponse>(`${this.baseEndpoint}/${params?.assetId}?${queryParams.toString()}`, undefined, 'PROJECTS')

      // Transform the paginated response to match the expected format
      if (response.success && response.data) {
        return {
          success: response.success,
          message: response.message,
          data: (response.data as any).data || []
        }
      }

      return {
        success: false,
        message: 'Failed to fetch scans',
        data: []
      }
    } catch (error) {
      console.error('Failed to fetch scans:', error)
      throw error
    }
  }

  async getScanById(scanId: string): Promise<ApiResponse<Scan>> {
    try {
      const response = await apiClient.get<Scan>(`${this.baseEndpoint}/scan/${scanId}`, undefined, 'PROJECTS')
      return response
    } catch (error) {
      console.error('Failed to fetch scan:', error)
      throw error
    }
  }

  async createScan(scanData: CreateScanRequest): Promise<ApiResponse<Scan>> {
    try {
      const response = await apiClient.post<Scan>(this.baseEndpoint, scanData, undefined, 'PROJECTS')
      return response
    } catch (error) {
      console.error('Failed to create scan:', error)
      throw error
    }
  }

  async updateScan(scanId: string, scanData: UpdateScanRequest): Promise<ApiResponse<Scan>> {
    try {
      const response = await apiClient.patch<Scan>(`${this.baseEndpoint}/${scanId}`, scanData, undefined, 'PROJECTS')
      return response
    } catch (error) {
      console.error('Failed to update scan:', error)
      throw error
    }
  }

  async deleteScan(scanId: string): Promise<ApiResponse<null>> {
    try {
      const response = await apiClient.delete<null>(`${this.baseEndpoint}/${scanId}`, undefined, 'PROJECTS')
      return response
    } catch (error) {
      console.error('Failed to delete scan:', error)
      throw error
    }
  }

  async createApplicationProfile(profileData: CreateApplicationProfileRequest): Promise<ApiResponse<ApplicationProfileResponse>> {
    try {
      const response = await apiClient.post<ApplicationProfileResponse>(
        `${this.baseEndpoint}/application-profile`,
        profileData,
        undefined,
        'PROJECTS'
      )
      return response
    } catch (error) {
      console.error('Failed to create application profile:', error)
      throw error
    }
  }

  async updateApplicationProfile(
    scanId: string,
    profileData: UpdateApplicationProfileRequest
  ): Promise<ApiResponse<ApplicationProfileResponse>> {
    try {
      const response = await apiClient.patch<ApplicationProfileResponse>(
        `${this.baseEndpoint}/${scanId}/application-profile`,
        profileData,
        undefined,
        'PROJECTS'
      )
      return response
    } catch (error) {
      console.error('Failed to update application profile:', error)
      throw error
    }
  }

  async getApplicationProfile(scanId: string): Promise<ApiResponse<ApplicationProfileResponse>> {
    try {
      const response = await apiClient.get<ApplicationProfileResponse>(
        `${this.baseEndpoint}/${scanId}/application-profile`,
        undefined,
        'PROJECTS'
      )
      return response
    } catch (error) {
      console.error('Failed to fetch application profile:', error)
      throw error
    }
  }
}

export const scansApi = new ScansApiService()
